import { FC, useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Route, Routes } from 'react-router'
import { useEffectOnce, useLocation } from 'react-use'

import { PrivateRouteGroup } from './@paco/compositions'
import Layout from './@paco/containers/Layout/Layout'
import { localStorageTokenKey } from './@paco/entities/Authentication/Authentication'
import { useIdleTimer } from './@paco/helpers/hooks/useIdleTimer'
import { InactiveUserPage, PincodePage } from './@paco/pages'
import Login from './@paco/pages/Login/Login'
import RequestPasswordReset from './@paco/pages/RequestPasswordReset/RequestPasswordReset'
import ResetPassword from './@paco/pages/ResetPassword/ResetPassword'
import SamlReturnPage from './@paco/pages/SamlReturn/SamlReturnPage'
import { getProfileOfCurrentUser } from './@paco/redux/authenticatedUser/authenticatedUserActions'
import { setError } from './@paco/redux/authenticatedUser/authenticatedUserReducer'
import useContractHours from './@paco/redux/contractHours/contractHoursHooks'
import authRoutes from './@paco/routes/subRoutes/authRoutes'
import { RoleType } from './@paco/types/roleTypes'
import { UserStatus } from './@paco/types/userStatus'
import CacheBuster from './components/CacheBuster/CacheBuster'
import { addModelCloseListeners, isTouch } from './helpers'
import ErrorPage from './pages/ErrorPage'
import {
    logout,
    setAppInit,
    setAppNotificationsInterval,
    setAppRouteChange,
} from './redux/app/appActions'
import { Reducers } from './redux/reducers'
import { useTypedDispatch, useTypedSelector } from './redux/store'

const App: FC = () => {
    const dispatch = useDispatch()
    const pacoDispatch = useTypedDispatch()
    const location = useLocation()
    useContractHours()
    useIdleTimer()

    const apiToken = localStorage.getItem(localStorageTokenKey) || sessionStorage.getItem(localStorageTokenKey)
    const appError = useSelector<Reducers, any>(state => state.appReducer.error)
    const {
        permittedUserRoutes,
        user,
        isUserIdle,
        error: authenticatedUserError,
    } = useTypedSelector(state => state.authenticatedUserReducer)

    const isAuthenticated = !!apiToken
    const isUserInactive = user?.status === UserStatus.inActive || user?.status === UserStatus.deleted
    const isViewer = user?.role?.slug === RoleType.viewer
    const appErrorCode = appError && parseInt(appError.code, 10)

    const isEligibleForPincode = user?.employeeNumber && !isViewer
    const shouldShowPinPrompt = isAuthenticated && isEligibleForPincode && isUserIdle
    const inertElementsWhenPinPromptShownSelector = '.paco-modal, .modal, .modal-backdrop'

    const loginPath = authRoutes.find(route => route.key === 'login')?.path
    const requestPasswordResetPath = authRoutes.find(route => route.key === 'requestPasswordReset')?.path
    const resetPasswordPath = authRoutes.find(route => route.key === 'resetPassword')?.path
    const samlReturnPath = authRoutes.find(route => route.key === 'samlReturn')?.path

    useEffectOnce((): void => {
        document.body.classList.add(isTouch() ? 'is-touch' : 'no-touch')
        addModelCloseListeners()
    })

    useEffect((): void => {
        if (apiToken) {
            dispatch(setAppInit())
            dispatch(setAppNotificationsInterval(dispatch))
            pacoDispatch(getProfileOfCurrentUser())
        }
    }, [apiToken])

    useEffect((): void => {
        dispatch(setAppRouteChange())
    }, [location.pathname])

    useEffect((): void => {
        if (authenticatedUserError) {
            dispatch(logout())
            dispatch(setError(''))
        }
    }, [authenticatedUserError])

    useEffect((): void => {
        if (
            user
            && !user.permissions.some(permission => permission.slug === 'use-manager-app')
            // Inactive 'viewer' users never have the 'use-manager-app' permission,
            // but should still see the InactiveUserPage before logging out
            && !(isUserInactive && isViewer)
        ) {
            dispatch(logout())
        }
    }, [user])

    useEffect(() => {
        if (shouldShowPinPrompt) {
            document.querySelectorAll<HTMLElement>(inertElementsWhenPinPromptShownSelector).forEach(modal => modal.setAttribute('inert', 'inert'))
        } else {
            document.querySelectorAll<HTMLElement>(inertElementsWhenPinPromptShownSelector).forEach(modal => modal.removeAttribute('inert'))
        }
    }, [shouldShowPinPrompt])

    if (isAuthenticated && isUserInactive) {
        return (
            <InactiveUserPage />
        )
    }

    if ((appErrorCode === 500) || (appErrorCode === 410)) {
        return (
            <Layout>
                <ErrorPage errorCode={appErrorCode} />
            </Layout>
        )
    }

    return (
        <CacheBuster>
            <>
                {shouldShowPinPrompt && <PincodePage />}
                {/* @ts-ignore */}
                <div inert={shouldShowPinPrompt ? 'inert' : undefined}>
                    <Routes>
                        {isAuthenticated ? (
                            <Route path="/*" element={<PrivateRouteGroup permittedUserRoutes={permittedUserRoutes} />} />
                        ) : (
                            <>
                                <Route path={loginPath} element={<Login />} />
                                <Route path={requestPasswordResetPath} element={<RequestPasswordReset />} />
                                <Route path={resetPasswordPath} element={<ResetPassword />} />
                                <Route path={samlReturnPath} element={<SamlReturnPage />} />
                                <Route path="/*" element={<Navigate to="/" />} />
                            </>
                        )}
                    </Routes>
                </div>
            </>
        </CacheBuster>
    )
}

export default App
